import { z } from 'zod';
import { normalizeTag } from '../lib/tagUtils.js';

/** Regex for valid tags: lowercase alphanumeric + hyphens/underscores, max 50 chars */
const tagRegex = /^[a-z0-9_-]+$/;
const TagSchema = z.string().regex(tagRegex).max(50);

/**
 * Input tag array schema: normalizes each tag, removes empties, deduplicates.
 * Undefined/missing input is treated as an empty array (handles optional fields).
 */
function makeTagArrayInput(maxItems: number) {
  return z.preprocess((raw) => {
    const arr = Array.isArray(raw) ? raw : [];
    return [
      ...new Set(
        arr
          .filter((t): t is string => typeof t === 'string')
          .map(normalizeTag)
          .filter((t) => t.length > 0),
      ),
    ];
  }, z.array(TagSchema).max(maxItems));
}

// ─── Song ─────────────────────────────────────────────────────────────────────

export const SongStatusSchema = z.enum(['pending', 'processing', 'ready', 'error']);
export type SongStatus = z.infer<typeof SongStatusSchema>;

/**
 * The community member who added a song to the library, resolved for display.
 *
 * Distinct from `sourceUploader`, which is the external channel name from yt-dlp.
 */
export const SongAddedBySchema = z.object({
  uid: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
});
export type SongAddedBy = z.infer<typeof SongAddedBySchema>;

export const SongSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(300),
  artist: z.string().max(200).default('Unknown'),
  album: z.string().max(200).optional(),
  coverArtUrl: z.string().max(1000).optional(),
  coverArtPath: z.string().max(500).optional(),
  coverArtMime: z.string().max(100).optional(),
  tags: z.array(TagSchema).max(30),
  notes: z.string().max(2000).optional(),
  sourceUrl: z.string().url().optional(),
  sourceTitle: z.string().max(500).optional(),
  sourceUploader: z.string().max(200).optional(),
  /**
   * What was cut from the track, in seconds against the original.
   *
   * The cut is baked into the stored file, so these describe what was removed rather
   * than offsets to apply at playback.
   */
  trimStartSeconds: z.number().nonnegative().optional(),
  trimEndSeconds: z.number().nonnegative().optional(),
  /** Preserved pre-trim file. Present only on a trimmed song. */
  originalFilePath: z.string().optional(),
  /** Firebase UID of the user who added this song. Admin-only — stripped for listeners. */
  addedByUid: z.string().optional(),
  /** Resolved form of `addedByUid`. Admin-only — stripped for listeners. */
  addedBy: SongAddedBySchema.optional(),
  filePath: z.string().optional(),
  durationSeconds: z.number().positive().optional(),
  status: SongStatusSchema,
  errorMessage: z.string().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Song = z.infer<typeof SongSchema>;

/**
 * What an unprivileged listener may see about a song.
 *
 * Enough to recognise a track, see how it is categorised, and follow it back to its
 * source. No notes, no pipeline status, no timestamps, no attribution, and nothing
 * about where the file lives. `library.read` holders get the full `SongSchema` from
 * the admin endpoint instead.
 *
 * Tags are public: they are how the community describes a track, they already ride
 * along in the queue and now-playing feeds, and the tag descriptions behind them are
 * served from a public endpoint.
 *
 * Derived from `SongSchema` by picking, so this is a strict subset — a field only
 * becomes public by being named here.
 */
export const PublicSongSchema = SongSchema.pick({
  id: true,
  title: true,
  artist: true,
  album: true,
  coverArtUrl: true,
  sourceUrl: true,
  durationSeconds: true,
  tags: true,
});
export type PublicSong = z.infer<typeof PublicSongSchema>;

/** Field names carried by the public projection, for server-side filtering. */
export const PUBLIC_SONG_FIELDS = Object.keys(PublicSongSchema.shape) as Array<keyof PublicSong>;

export const CreateSongSchema = z.object({
  title: z.string().min(1).max(300),
  artist: z.string().max(200).optional(),
  album: z.string().max(200).optional(),
  tags: makeTagArrayInput(30),
  notes: z.string().max(2000).optional(),
  sourceUrl: z.string().url().optional(),
  sourceTitle: z.string().max(500).optional(),
  sourceUploader: z.string().max(200).optional(),
});
export type CreateSongInput = z.infer<typeof CreateSongSchema>;

export const UpdateSongSchema = CreateSongSchema.partial();
export type UpdateSongInput = z.infer<typeof UpdateSongSchema>;

/**
 * Body for reassigning who added a song.
 *
 * Deliberately separate from `UpdateSongSchema`: that schema is derived from
 * `CreateSongSchema`, which the upload route parses from client-supplied metadata.
 * Attribution must not be settable through the general edit path.
 */
export const ReassignSongUploaderSchema = z.object({
  uid: z.string().min(1).max(128).nullable(),
});
export type ReassignSongUploaderInput = z.infer<typeof ReassignSongUploaderSchema>;

/**
 * Body for trimming a song.
 *
 * Deliberately its own schema rather than fields on `UpdateSongSchema`: applying it
 * rewrites the audio file, which is a different kind of act from editing a title and
 * should not ride along on a metadata save.
 */
export const TrimSongSchema = z.object({
  startSeconds: z.number().nonnegative(),
  endSeconds: z.number().positive(),
});
export type TrimSongInput = z.infer<typeof TrimSongSchema>;

export const SongFiltersSchema = z.object({
  tags: z.array(z.string()).optional(),
  status: SongStatusSchema.optional(),
  search: z.string().max(200).optional(),
  sortBy: z
    .enum(['createdAt', 'updatedAt', 'title', 'artist', 'status', 'durationSeconds'])
    .default('createdAt'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type SongFilters = z.infer<typeof SongFiltersSchema>;

export const TagSummarySchema = z.object({
  tag: TagSchema,
  count: z.number().int().min(0),
  description: z.string().max(500).nullable().optional(),
});
export type TagSummary = z.infer<typeof TagSummarySchema>;

export const UpdateTagMetadataSchema = z.object({
  description: z.string().max(500).optional(),
});
export type UpdateTagMetadataInput = z.infer<typeof UpdateTagMetadataSchema>;
